"use client";

import { isClient } from "./is-client";

type StorageApi = "localStorage" | "sessionStorage";
type GetterSetterOptions = { expectJson: boolean };

// Internal helper to avoid repetition in base methods
const getStorage = (api: StorageApi) => (isClient() ? window[api] : null);

const base = {
  get: (api: StorageApi, key: string, options?: GetterSetterOptions) => {
    const storage = getStorage(api);
    if (!storage || !key) return undefined;

    const item = storage.getItem(key);
    if (item === null || item === "undefined") return undefined;

    if (options?.expectJson) {
      try {
        return JSON.parse(item);
      } catch (_e) {
        return undefined;
      }
    }
    return item;
  },
  set: (api: StorageApi, key: string, value: unknown, options?: GetterSetterOptions) => {
    const storage = getStorage(api);
    if (!storage || !key) return;

    const serialized = options?.expectJson ? JSON.stringify(value) : String(value);
    storage.setItem(key, serialized);
  },
  remove: (api: StorageApi, key: string) => getStorage(api)?.removeItem(key),
};

/**
 * Factory to create the helper objects
 */
function createStorageHelper<T = string>(api: StorageApi, key: string, isJson = false) {
  return {
    key,
    get: () => base.get(api, key, { expectJson: isJson }) as T | undefined,
    set: (value: T) => base.set(api, key, value, { expectJson: isJson }),
    remove: () => base.remove(api, key),
  };
}

// Base Helpers
export const localStorageHelpers = {
  get: (key: string, opts?: GetterSetterOptions) => base.get("localStorage", key, opts),
  set: (key: string, val: unknown, opts?: GetterSetterOptions) => base.set("localStorage", key, val, opts),
  remove: (key: string) => base.remove("localStorage", key),
};

export const sessionStorageHelpers = {
  get: (key: string, opts?: GetterSetterOptions) => base.get("sessionStorage", key, opts),
  set: (key: string, val: unknown, opts?: GetterSetterOptions) => base.set("sessionStorage", key, val, opts),
  remove: (key: string) => base.remove("sessionStorage", key),
};

// --- Local Storage Instances ---
export const localStorageLastUser = createStorageHelper("localStorage", "__agora_last_user");

export const localStorageLastKnown = createStorageHelper<Record<string, unknown>>(
  "localStorage",
  "__agora_last_known",
  true,
);

// --- Session Storage Instances ---
export const sessionStorageSessionId = createStorageHelper("sessionStorage", "__agora_session_id");

export const sessionAppSessionStartedTimestamp = {
  ...createStorageHelper("sessionStorage", "__agora_session_timestamp"),
  set: () => base.set("sessionStorage", "__agora_session_timestamp", Date.now()), // Override for specific logic
};

export const sessionStorageFeatureFlags = createStorageHelper<Record<string, string | boolean>>(
  "sessionStorage",
  "__agora_feature_flags",
  true,
);

export const sessionStorageActiveFeatureFlags = createStorageHelper<string[]>(
  "sessionStorage",
  "__agora_active_feature_flags",
  true,
);

export const sessionStorageCountry = createStorageHelper<string>("sessionStorage", "__agora_lc");

export const sessionStorageOrgId = createStorageHelper<string>("sessionStorage", "__agora_org_id");

export const sessionStorageOrgName = createStorageHelper<string>("sessionStorage", "__agora_org_name");

export const sessionStorageRegion = createStorageHelper<string>("sessionStorage", "__agora_lr");

export const sessionStorageUserId = createStorageHelper<string>("sessionStorage", "__agora_user_id");

export const sessionStorageUserEmail = createStorageHelper<string>("sessionStorage", "__agora_user_email");

export const sessionStorageEventIndex = {
  ...createStorageHelper<number>("sessionStorage", "__agora_session_event_index"),

  // Custom incrementer to handle the read-update-write cycle
  increment: (): number => {
    const key = "__agora_session_event_index";
    const current = Number(base.get("sessionStorage", key)) || 0;
    const next = current + 1;
    base.set("sessionStorage", key, next);
    return next;
  },

  // Helper to get current value as a number (defaults to 0)
  get: (): number => {
    return Number(base.get("sessionStorage", "__agora_session_event_index")) || 0;
  },
};
